/**
 * IndicationsTicket — Indications ticket content for EditorPanel.
 * Extracted from EditorPanel.tsx (refactoring, no behavior changes).
 */

import { useEffect, useState } from 'react';
import { markerGridFit } from '../../../utils/gridFit';
import { planTopologyUpdate } from '../../../domain/topology/applyDerivedTopology';
import { planSiteRederive, assembleRederivePatches, type SiteRederivePlan } from '../../../domain/topology/planSiteRederive';
import SiteRederiveModal from '../SiteRederiveModal';
import { useProjectStore, useUIStore, useMapStore, INDICATION_TYPES, STATUS_LABELS, STATUS_ORDER } from '../../../stores';
import type { Indication, IndicationType, IndicationStatus } from '../../../stores/projectStore';
import { batchApplyIndications, undoOperation, listOperations } from '../../../services/operationsApi';
import type { BatchApplyItem, OperationSummary } from '../../../services/operationsApi';
import { fetchEntityIndications } from '../../../services/indicationApi';
import { useIsReadOnlyViewer } from '../../../hooks/useShareViewer';
import { Button } from '../../common';
import { rotatePolygonAroundCenter, centroidOfPolygon, rectGeomFromCenter } from '../../../utils';
import { captureDay } from '../../../utils/captureDate';
import { observationDays, observedOnVisibleDay } from '../../../utils/observations';

/** Format a YYYY-MM-DD day as "Jul 12, 2026"; "No date" for empty. */
function formatDay(day: string): string {
  if (!day) return 'No date';
  const [y, m, d] = day.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return Number.isNaN(dt.getTime())
    ? day
    : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Current local time as a datetime-local value ("YYYY-MM-DDTHH:MM"). */
function localDatetimeNow(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function IndicationsTicket() {
  const isReadOnly = useIsReadOnlyViewer();
  const indications = useProjectStore((s) => s.data.indications);
  const availableDates = useProjectStore((s) => s.availableDates);
  const visibleDates = useProjectStore((s) => s.visibleDates);
  const clearProject = useProjectStore((s) => s.clearProject);

  const isAddMode = useUIStore((s) => s.isAddMode);
  const quickAddIndicationType = useUIStore((s) => s.quickAddIndicationType);
  const defaultIndicationOrientation = useUIStore((s) => s.defaultIndicationOrientation);
  const defaultStringModules = useUIStore((s) => s.defaultStringModules);
  const defaultObservationTime = useUIStore((s) => s.defaultObservationTime);
  const batchStatusMode = useUIStore((s) => s.batchStatusMode);
  const batchStatusTarget = useUIStore((s) => s.batchStatusTarget);
  const setAddMode = useUIStore((s) => s.setAddMode);
  const setQuickAddIndicationType = useUIStore((s) => s.setQuickAddIndicationType);
  const setBatchStatusMode = useUIStore((s) => s.setBatchStatusMode);
  const setBatchStatusTarget = useUIStore((s) => s.setBatchStatusTarget);
  const setDefaultIndicationOrientation = useUIStore((s) => s.setDefaultIndicationOrientation);
  const setDefaultStringModules = useUIStore((s) => s.setDefaultStringModules);
  const setDefaultObservationTime = useUIStore((s) => s.setDefaultObservationTime);
  const addNotification = useUIStore((s) => s.addNotification);
  const showConfirm = useUIStore((s) => s.showConfirm);
  const openPanel = useUIStore((s) => s.openPanel);

  const selectedFeatureId = useMapStore((s) => s.selectedFeatureId);
  const selectedFeatureIds = useMapStore((s) => s.selectedFeatureIds);
  const indicationFilters = useMapStore((s) => s.indicationFilters);
  const areaSelectMode = useMapStore((s) => s.areaSelectMode);
  const setAreaSelectMode = useMapStore((s) => s.setAreaSelectMode);

  // Guards double-clicks while the sequential apply-to-selected loop runs.
  const [isApplyingStatus, setIsApplyingStatus] = useState(false);

  // Escape exits add mode (popup or quick-add flavor). No-op while a modal is
  // open so an open IndicationPopup keeps its own Escape semantics.
  useEffect(() => {
    if (!isAddMode) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (useUIStore.getState().activeModal) return;
      setAddMode(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isAddMode, setAddMode]);

  // Escape exits batch status paint mode. Same modal guard as add mode; the
  // two handlers never coexist (arming one mode disarms the other).
  useEffect(() => {
    if (!batchStatusMode) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (useUIStore.getState().activeModal) return;
      setBatchStatusMode(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [batchStatusMode, setBatchStatusMode]);

  // ============================================
  // HELPERS
  // ============================================
  const downloadBlob = (filename: string, blob: Blob) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const csvEscape = (val: string | number | null | undefined): string => {
    if (val === null || val === undefined) return '';
    // Numbers (e.g. lng/lat) are emitted verbatim so negative coordinates are
    // not mistaken for a formula by the guard below.
    const isNumber = typeof val === 'number';
    let str = String(val);
    // Neutralize spreadsheet formula injection: a text cell beginning with a
    // formula trigger (= + - @ TAB CR) is prefixed with a single quote so
    // Excel/Sheets render it as literal text instead of evaluating it.
    if (!isNumber && /^[=+\-@\t\r]/.test(str)) str = `'${str}`;
    if (str.includes(',') || str.includes('"') || str.includes('\n')) return `"${str.replace(/"/g, '""')}"`;
    return str;
  };

  // Format an ISO timestamp as local "YYYY-MM-DD HH:MM" for the CSV lifecycle
  // columns; empty string for a missing/unparseable value.
  const fmtTime = (iso: string | undefined): string => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const indicationsToCSV = (): string => {
    // STORED VALUES ONLY (owner ruling 2026-09-02): the file must show
    // exactly what the screen shows — the old export back-filled blanks
    // from the proximity engine transiently, so the CSV disagreed with the
    // UI (a cross-surface disagreement in the same shape as the bug this
    // arc closed). Photo markers are excluded: they are proof-photo
    // containers, not faults (owner). mppt column added (was silently
    // absent from the export entirely).
    const headers = ['id', 'ind_type', 'station', 'inverter', 'mppt', 'dccb', 'string', 'module', 'notes', 'lng', 'lat', 'capture_date', 'status', 'dispatched', 'resolved'];
    const lines = [headers.join(',')];

    for (const ind of indications) {
      if (ind.type === 'Photo') continue;
      let lng: number | null = null;
      let lat: number | null = null;
      if (ind.geometry.type === 'Point') {
        [lng, lat] = ind.geometry.coordinates as [number, number];
      } else if (ind.geometry.type === 'Polygon') {
        const c = centroidOfPolygon(ind.geometry.coordinates[0] as [number, number][]);
        if (c) [lng, lat] = c;
      }

      const station = ind.properties.station;
      const inverter = ind.properties.inverter;
      const mppt = ind.properties.mppt;
      const dccb = ind.properties.dccb;
      const string_ = ind.properties.string;
      const module_ = ind.properties.module;

      // For 'Custom' types, export the user-provided name so the CSV row is
      // meaningful (otherwise every custom finding reads as "Custom").
      const typeLabel = ind.type === 'Custom' && ind.properties.customTypeName?.trim()
        ? ind.properties.customTypeName.trim()
        : ind.type;

      // Resolution lifecycle (Phase 1): current status + the created/dispatched/
      // resolved timestamps derived from the server-stamped statusEvents log.
      // dispatched = first transition to In progress; resolved = the latest
      // Resolved transition.
      const events = ind.properties.statusEvents ?? [];
      const status = ind.properties.status ?? 'open';
      const statusLabel = status === 'resolved' ? 'Resolved' : status === 'in_progress' ? 'In progress' : 'Open';
      const dispatchedAt = events.find((e) => e.status === 'in_progress')?.at;
      const resolvedAt = [...events].reverse().find((e) => e.status === 'resolved')?.at;

      lines.push([
        csvEscape(ind.id), csvEscape(typeLabel), csvEscape(station), csvEscape(inverter),
        csvEscape(mppt), csvEscape(dccb), csvEscape(string_), csvEscape(module_), csvEscape(ind.properties.description),
        csvEscape(lng), csvEscape(lat), csvEscape(captureDay(ind.properties.captureDate)),
        csvEscape(statusLabel),
        csvEscape(fmtTime(dispatchedAt)), csvEscape(fmtTime(resolvedAt)),
      ].join(','));
    }
    return lines.join('\n');
  };

  // ============================================
  // ORIENTATION
  // ============================================
  const rotateGeometry = (
    geometry: GeoJSON.Geometry,
    angleDegrees: number
  ): GeoJSON.Geometry => {
    if (geometry.type !== 'Polygon') return geometry;
    const coords = geometry.coordinates[0];
    const center = centroidOfPolygon(coords);
    if (!center || !angleDegrees) return geometry;
    return rotatePolygonAroundCenter(geometry as GeoJSON.Polygon, center, angleDegrees);
  };

  // Scope orientation "Apply to shown" to the indications CURRENTLY ON THE MAP —
  // the active campaign(s) via the date filter, plus the type filter and hidden
  // reviewed-Photo containers. This stops re-correcting a new campaign from also
  // re-rotating an already-corrected earlier one (filter to that campaign first).
  const orientationTargets = () => {
    const visibleSet = new Set(visibleDates);
    const hasAnyDates = availableDates.length > 0;
    return indications.filter((i) => {
      if (i.geometry.type !== 'Polygon') return false;
      if (i.type === 'Photo' && i.properties.reviewed) return false;
      if (indicationFilters[i.type] === false) return false;
      if (hasAnyDates && !observedOnVisibleDay(i, visibleSet)) return false;
      return true;
    });
  };

  // P0 guardrail (undo/history plan, 2026-07-30): a batch gesture above this
  // many targets requires an explicit count-confirm first.
  const BATCH_CONFIRM_THRESHOLD = 100;
  // P2: server-backed batch gestures ride /indications/batch-apply in chunks —
  // one operation id, per-item CAS, prior-state capture → one-click Undo.
  const BATCH_CHUNK = 200;

  // Minimal operation history (P2): recent batch operations stay undoable here
  // even after their toast expires. Loaded on demand, refreshed after undo.
  const [opsOpen, setOpsOpen] = useState(false);
  const [ops, setOps] = useState<OperationSummary[] | null>(null);
  const loadOps = async () => {
    const sid = useProjectStore.getState().meta.siteId;
    if (!sid) return;
    try {
      setOps(await listOperations(sid, 15));
    } catch {
      setOps([]);
    }
  };
  const toggleOps = () => {
    const next = !opsOpen;
    setOpsOpen(next);
    if (next) void loadOps();
  };

  // Undo an operation, then refetch the authoritative rows (the undo response
  // carries versions, not content — and restored statusEvents live server-side).
  const runUndo = async (operationId: string) => {
    const sid = useProjectStore.getState().meta.siteId;
    if (!sid) return;
    try {
      const res = await undoOperation(sid, operationId);
      const { indications: fresh } = await fetchEntityIndications(sid);
      useProjectStore.getState().replaceIndications(fresh);
      addNotification({
        type: res.conflicts.length ? 'warning' : 'success',
        title: `Undo: ${res.restored} indication${res.restored === 1 ? '' : 's'} restored`,
        message: res.conflicts.length ? `${res.conflicts.length} kept (modified since the operation)` : undefined,
      });
    } catch (err) {
      addNotification({ type: 'error', title: 'Undo failed', message: err instanceof Error ? err.message : 'Unknown error' });
    }
    if (opsOpen) void loadOps();
  };

  /** Post prepared items as one operation (sequential chunks), mirror the
   *  confirmed rows locally, and toast with an inline Undo action. */
  const runBatchOperation = async (opts: {
    kind: 'batch_rotate' | 'batch_status' | 'batch_fit' | 'bulk_rederive';
    label: string;
    items: BatchApplyItem[];
    localRowFor: (item: BatchApplyItem, newVersion: number) => Parameters<ReturnType<typeof useProjectStore.getState>['applyServerIndicationRows']>[0][number];
    successTitle: (applied: number) => string;
  }) => {
    const sid = useProjectStore.getState().meta.siteId;
    if (!sid) return;
    const operationId = crypto.randomUUID();
    const versionById = new Map<string, number>();
    let applied = 0;
    let stale = 0;
    let failedChunks = 0;
    setIsApplyingStatus(true);
    try {
      for (let i = 0; i < opts.items.length; i += BATCH_CHUNK) {
        try {
          const res = await batchApplyIndications(sid, operationId, opts.kind, opts.label, opts.items.slice(i, i + BATCH_CHUNK));
          applied += res.applied;
          stale += res.staleConflicts.length + res.missing.length;
          for (const r of res.results) versionById.set(r.id, r.version);
        } catch {
          failedChunks++;
        }
      }
    } finally {
      setIsApplyingStatus(false);
    }
    useProjectStore.getState().applyServerIndicationRows(
      opts.items
        .filter((it) => versionById.has(it.id))
        .map((it) => opts.localRowFor(it, versionById.get(it.id)!)),
    );
    const parts: string[] = [];
    if (stale) parts.push(`${stale} skipped (changed elsewhere)`);
    if (failedChunks) parts.push(`${failedChunks} chunk(s) failed — re-apply to retry`);
    addNotification({
      type: failedChunks || stale ? 'warning' : applied ? 'success' : 'info',
      title: opts.successTitle(applied),
      message: parts.length ? parts.join(', ') : undefined,
      duration: 30000,
      action: applied > 0 ? { label: 'Undo', onClick: () => { void runUndo(operationId); } } : undefined,
    });
    if (opsOpen) void loadOps();
  };

  /** Rotate targets to newDeg — batch-apply for server sites, the legacy
   *  per-row local path for blob-only projects (no siteId). */
  const runRotate = (targets: Indication[], newDeg: number, successMessage: string, noChangeMessage: string) => {
    const sid = useProjectStore.getState().meta.siteId;
    const rotatable = targets.filter(
      (ind) => ind.geometry.type === 'Polygon' && (ind.properties.rotationDeg || 0) !== newDeg,
    );
    if (rotatable.length === 0) {
      addNotification({ type: 'info', title: 'No changes', message: noChangeMessage });
      return;
    }
    if (!sid) {
      // Local project: original optimistic per-row path.
      for (const ind of rotatable) {
        const delta = newDeg - (ind.properties.rotationDeg || 0);
        const rotatedGeom = rotateGeometry(ind.geometry, delta);
        useProjectStore.getState().updateIndication(ind.id, { geometry: rotatedGeom, properties: { rotationDeg: newDeg } });
      }
      addNotification({ type: 'success', title: `Rotated ${rotatable.length} indication(s)`, message: successMessage });
      return;
    }
    const now = new Date().toISOString();
    const rowsById = new Map<string, { geometry: GeoJSON.Geometry }>();
    const items: BatchApplyItem[] = [];
    for (const ind of rotatable) {
      if (!ind._version) continue; // not yet server-confirmed — skip
      const delta = newDeg - (ind.properties.rotationDeg || 0);
      const rotatedGeom = rotateGeometry(ind.geometry, delta);
      rowsById.set(ind.id, { geometry: rotatedGeom });
      items.push({
        id: ind.id,
        version: ind._version,
        geometry_json: JSON.stringify(rotatedGeom),
        properties_json: JSON.stringify({ ...ind.properties, rotationDeg: newDeg, updatedAt: now }),
      });
    }
    if (items.length === 0) {
      addNotification({ type: 'warning', title: 'Nothing to rotate', message: 'Selected indications are not yet synced to the server.' });
      return;
    }
    void runBatchOperation({
      kind: 'batch_rotate',
      label: `Rotated ${items.length} indications to ${newDeg}°`,
      items,
      localRowFor: (it, v) => ({ id: it.id, geometry: rowsById.get(it.id)!.geometry, properties: { rotationDeg: newDeg }, _version: v }),
      successTitle: (n) => `Rotated ${n} indication(s) — ${successMessage}`,
    });
  };

  /**
   * The per-site bulk corrector ("Re-derive site from grid…"). Safety
   * contracts live in planSiteRederive (c6-reviewed before first run);
   * this handler only computes the plan, shows the consent modal, and
   * applies the APPROVED patches as ONE undoable bulk_rederive operation.
   */
  const [rederivePlan, setRederivePlan] = useState<SiteRederivePlan | null>(null);

  const handleRederiveSite = () => {
    const d = useProjectStore.getState().data;
    const layouts = d.siteLayouts ?? (d.siteLayout ? [d.siteLayout] : []);
    if (!layouts.some((l) => l.moduleGridConfig && l.transform)) {
      addNotification({ type: 'warning', title: 'No module grid', message: 'Re-derive needs a layout with a configured module grid.' });
      return;
    }
    setIsApplyingStatus(true);
    // plan computation is CPU-bound (a derive per fault) — yield a frame so
    // the busy state paints before the scan starts
    window.setTimeout(() => {
      try {
        setRederivePlan(planSiteRederive(indications, layouts));
      } finally {
        setIsApplyingStatus(false);
      }
    }, 30);
  };

  const executeRederive = (opts: { acceptTier: boolean; includeFlagged: boolean; clearFields: Set<string> }) => {
    const plan = rederivePlan;
    setRederivePlan(null);
    if (!plan) return;
    const patches = assembleRederivePatches(plan, opts);
    if (patches.length === 0) {
      addNotification({ type: 'info', title: 'Nothing to apply' });
      return;
    }
    const byId = new Map(indications.map((i) => [i.id, i]));
    const now = new Date().toISOString();
    const items: BatchApplyItem[] = [];
    const patchById = new Map<string, Record<string, unknown>>();
    for (const { id, patch } of patches) {
      const ind = byId.get(id);
      if (!ind || !ind._version) continue;
      patchById.set(id, patch);
      items.push({
        id,
        version: ind._version,
        properties_json: JSON.stringify({ ...ind.properties, ...patch, updatedAt: now }),
      });
    }
    if (items.length === 0) {
      addNotification({ type: 'warning', title: 'Nothing to apply', message: 'Affected indications are not yet synced to the server.' });
      return;
    }
    void runBatchOperation({
      kind: 'bulk_rederive',
      label: `Re-derived ${items.length} indications from grid`,
      items,
      localRowFor: (it, v) => ({
        id: it.id,
        properties: patchById.get(it.id)!,
        _version: v,
      }),
      successTitle: (n) => `Re-derived ${n} indication(s) from the grid`,
    });
  };

  /**
   * T-14 step 3: Fit the SHOWN markers to their grid cells — size + bearing
   * from the cell under each marker's center, POSITION UNTOUCHED. ONE
   * undoable batch_fit operation (a bulk geometry change without a single
   * undo entry would be the most alarming thing in this arc). Topology is
   * NEVER changed by this button — the report says how many marks now
   * derive differently, and those flow through the normal chip path.
   * String OC (since 2026-09-14) takes the FULL footprint of the string the
   * derive names — the grid's own count/rows/bearing for that string, never
   * the site-wide default (the v1 skip reason); see utils/gridFit.
   */
  const handleFitToGrid = () => {
    const sid = useProjectStore.getState().meta.siteId;
    const d = useProjectStore.getState().data;
    const layouts = d.siteLayouts ?? (d.siteLayout ? [d.siteLayout] : []);
    if (layouts.length === 0) {
      addNotification({ type: 'warning', title: 'No layout', message: 'Fit to grid needs a vector layout with a module grid.' });
      return;
    }
    const SKIP_TYPES = new Set(['Photo', 'Video', 'DataPoint']);
    const targets = orientationTargets().filter((i) => !SKIP_TYPES.has(i.type));
    if (targets.length === 0) {
      addNotification({ type: 'warning', title: 'No shown markers to fit', message: 'Adjust the date/type filters so the markers you want are visible.' });
      return;
    }
    const run = () => {
      const now = new Date().toISOString();
      const rowsById = new Map<string, { geometry: GeoJSON.Geometry; rotationDeg: number }>();
      const items: BatchApplyItem[] = [];
      let offGrid = 0;
      let alreadyFit = 0;
      let derivesMoved = 0;
      for (const ind of targets) {
        if (!ind._version) continue;
        if (ind.geometry.type !== 'Polygon') continue;
        const center = centroidOfPolygon(ind.geometry.coordinates[0] as [number, number][]);
        if (!center) continue;
        const fit = markerGridFit(ind, layouts, center);
        if (!fit) { offGrid++; continue; }
        // no-op guard: already at this size + bearing (2cm / 0.5°)
        const ring = ind.geometry.coordinates[0] as [number, number][];
        const mpd = 111320 * Math.cos((center[1] * Math.PI) / 180);
        const eLen = (i: number, j: number) => Math.hypot(
          (ring[j][0] - ring[i][0]) * mpd,
          (ring[j][1] - ring[i][1]) * 111320,
        );
        const rotNow = ind.properties.rotationDeg || 0;
        const rotDiff = Math.abs(((rotNow - fit.directionDeg + 540) % 360) - 180);
        if (
          ring.length >= 4
          && Math.abs(eLen(0, 1) - fit.w) < 0.02
          && Math.abs(eLen(1, 2) - fit.h) < 0.02
          && rotDiff < 0.5
        ) { alreadyFit++; continue; }
        const base = rectGeomFromCenter(center[0], center[1], fit.w, fit.h);
        const newGeom = fit.directionDeg
          ? rotatePolygonAroundCenter(base, center, fit.directionDeg)
          : base;
        // report input: does the refit MOVE the derived answer? (derive is
        // scoped per marker — fine at campaign scale; revisit with a shared
        // cell index if a whole-site fit measures slow)
        const before = planTopologyUpdate(ind, layouts);
        const after = planTopologyUpdate({ ...ind, geometry: newGeom }, layouts);
        const b = before?.result.kind === 'grid' ? before.result : null;
        const a = after?.result.kind === 'grid' ? after.result : null;
        if (
          (b?.cell.entityId ?? null) !== (a?.cell.entityId ?? null)
          || (b !== null && a !== null && !b.isString && !a.isString
              && b.cell.moduleNumber !== a.cell.moduleNumber)
        ) derivesMoved++;
        rowsById.set(ind.id, { geometry: newGeom, rotationDeg: fit.directionDeg });
        items.push({
          id: ind.id,
          version: ind._version,
          geometry_json: JSON.stringify(newGeom),
          properties_json: JSON.stringify({ ...ind.properties, rotationDeg: fit.directionDeg, updatedAt: now }),
        });
      }
      if (items.length === 0) {
        addNotification({
          type: 'info',
          title: 'Nothing to fit',
          message: `${alreadyFit} already fitted, ${offGrid} off-grid (kept as-is).`,
        });
        return;
      }
      const reportParts = [
        `${derivesMoved} now derive a different string/module (see their chips)`,
        ...(alreadyFit ? [`${alreadyFit} already fitted`] : []),
        ...(offGrid ? [`${offGrid} off-grid kept as-is`] : []),
      ];
      if (!sid) {
        for (const [id, row] of rowsById) {
          useProjectStore.getState().updateIndication(id, {
            geometry: row.geometry,
            properties: { rotationDeg: row.rotationDeg },
          });
        }
        addNotification({ type: 'success', title: `Fitted ${rowsById.size} marker(s) to the grid`, message: reportParts.join(', ') });
        return;
      }
      void runBatchOperation({
        kind: 'batch_fit',
        label: `Fit ${items.length} markers to grid`,
        items,
        localRowFor: (it, v) => ({
          id: it.id,
          geometry: rowsById.get(it.id)!.geometry,
          properties: { rotationDeg: rowsById.get(it.id)!.rotationDeg },
          _version: v,
        }),
        successTitle: (n) => `Fitted ${n} marker(s) to the grid — ${reportParts.join(', ')}`,
      });
    };
    if (targets.length > BATCH_CONFIRM_THRESHOLD) {
      showConfirm({
        title: 'Fit markers to grid',
        message: `Resize and re-orient ${targets.length} shown markers to their module cells (String OC: the full span of its string)? Positions and topology values are not changed; one Undo reverts everything.`,
        confirmLabel: 'Fit markers',
        cancelLabel: 'Cancel',
        onConfirm: run,
      });
    } else {
      run();
    }
  };

  const handleApplyOrientationToAll = () => {
    const targets = orientationTargets();
    if (targets.length === 0) {
      addNotification({ type: 'warning', title: 'No shown indications to rotate', message: 'Adjust the date/type filters so the markers you want are visible.' });
      return;
    }
    const newDeg = defaultIndicationOrientation;
    const run = () => runRotate(targets, newDeg, `applied ${newDeg}° to the shown markers`, 'The shown indications are already at this orientation');
    if (targets.length > BATCH_CONFIRM_THRESHOLD) {
      showConfirm({
        title: `Rotate ${targets.length} indications?`,
        message: `This will apply ${newDeg}° to all ${targets.length} indications currently shown on the map (per the date + type filters), overwriting their current orientations. Narrow the filters first if you only meant one campaign.`,
        confirmLabel: `Rotate ${targets.length}`,
        variant: 'danger',
        onConfirm: run,
      });
      return;
    }
    run();
  };

  const handleApplyOrientationToSelected = () => {
    const selectedIds = selectedFeatureIds.length > 0 ? selectedFeatureIds : (selectedFeatureId ? [selectedFeatureId] : []);
    if (selectedIds.length === 0) {
      addNotification({ type: 'warning', title: 'No indication selected', message: 'Click an indication on the map first (hold Ctrl for multi-select)' });
      return;
    }
    const newDeg = defaultIndicationOrientation;
    const run = () => {
      const targets = selectedIds
        .map((id) => indications.find((ind) => ind.id === id))
        .filter((ind): ind is Indication => !!ind);
      runRotate(targets, newDeg, `applied ${newDeg}° orientation`, 'All selected indications already at this orientation');
    };
    if (selectedIds.length > BATCH_CONFIRM_THRESHOLD) {
      showConfirm({
        title: `Rotate ${selectedIds.length} selected indications?`,
        message: `This will apply ${newDeg}° to ${selectedIds.length} selected indications, overwriting their current orientations.`,
        confirmLabel: `Rotate ${selectedIds.length}`,
        variant: 'danger',
        onConfirm: run,
      });
      return;
    }
    run();
  };

  // ============================================
  // BATCH STATUS
  // ============================================
  // Applies batchStatusTarget to every selected indication. Independent of
  // paint mode being armed — shares the same target select. Sequential awaits:
  // setIndicationStatus reads _version from live store state per call, so no
  // stale-version races between items.
  const handleApplyStatusToSelected = () => {
    const selectedIds = selectedFeatureIds.length > 0 ? selectedFeatureIds : (selectedFeatureId ? [selectedFeatureId] : []);
    if (selectedIds.length === 0) {
      addNotification({ type: 'warning', title: 'No indication selected', message: 'Click an indication on the map first (hold Ctrl for multi-select)' });
      return;
    }
    if (selectedIds.length > BATCH_CONFIRM_THRESHOLD) {
      showConfirm({
        title: `Change status of ${selectedIds.length} indications?`,
        message: `This will set ${selectedIds.length} selected indications to "${STATUS_LABELS[batchStatusTarget]}".`,
        confirmLabel: `Apply to ${selectedIds.length}`,
        variant: 'danger',
        onConfirm: () => { void runApplyStatus(selectedIds); },
      });
      return;
    }
    void runApplyStatus(selectedIds);
  };

  const runApplyStatus = async (selectedIds: string[]) => {
    const target = batchStatusTarget;
    const label = STATUS_LABELS[target];
    const sid = useProjectStore.getState().meta.siteId;
    const all = useProjectStore.getState().data.indications;
    const targets = selectedIds
      .map((id) => all.find((i) => i.id === id))
      .filter((ind): ind is Indication => !!ind);
    const already = targets.filter((ind) => (ind.properties.status ?? 'open') === target).length;
    const toChange = targets.filter((ind) => (ind.properties.status ?? 'open') !== target);

    if (!sid) {
      // Local project: original sequential per-row path.
      setIsApplyingStatus(true);
      let updated = 0;
      let failed = 0;
      try {
        for (const ind of toChange) {
          const result = await useProjectStore.getState().setIndicationStatus(ind.id, target);
          if (result === 'updated' || result === 'local') updated++;
          else if (result !== 'noop') failed++;
        }
      } finally {
        setIsApplyingStatus(false);
      }
      const parts = [`${updated} updated`];
      if (already) parts.push(`${already} already ${label.toLowerCase()}`);
      if (failed) parts.push(`${failed} failed`);
      addNotification({ type: failed ? 'warning' : updated ? 'success' : 'info', title: `Status → ${label}`, message: parts.join(', ') });
      return;
    }

    const items: BatchApplyItem[] = toChange
      .filter((ind) => !!ind._version)
      .map((ind) => ({ id: ind.id, version: ind._version!, status: target }));
    if (items.length === 0) {
      addNotification({ type: 'info', title: `Status → ${label}`, message: already ? `${already} already ${label.toLowerCase()}` : 'Nothing to update' });
      return;
    }
    // Local mirror stamps only the status field; the server-stamped
    // statusEvents entries hydrate on the next project load.
    await runBatchOperation({
      kind: 'batch_status',
      label: `Status → ${label} (${items.length} indications)`,
      items,
      localRowFor: (it, v) => ({ id: it.id, properties: { status: target }, _version: v }),
      successTitle: (n) => `Status → ${label}: ${n} updated${already ? `, ${already} already ${label.toLowerCase()}` : ''}`,
    });
  };

  // ============================================
  // ACTIONS
  // ============================================
  const handleAddModeToggle = () => {
    setAddMode(!isAddMode);
  };

  const handleExportCSV = () => {
    const csv = indicationsToCSV();
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    downloadBlob('inspection_indications.csv', blob);
    addNotification({ type: 'success', title: 'Exported CSV' });
  };

  const handleClear = () => {
    showConfirm({
      title: 'Clear All Indications',
      message: 'Are you sure you want to clear all indications? This cannot be undone.',
      variant: 'danger',
      confirmLabel: 'Clear',
      onConfirm: () => {
        clearProject();
        addNotification({ type: 'info', title: 'All indications cleared' });
      },
    });
  };

  // ============================================
  // COMPUTED VALUES
  // ============================================
  // Counts = findings at the selected inspection (scope), incl. resolved; the
  // open/resolved split is an overlay (per-date rows below), NOT discounted — so
  // a past inspection stays true to what was found then.
  const indicationCounts = Object.entries(INDICATION_TYPES).map(([type, config]) => ({
    type, label: config.label, color: config.color,
    count: indications.filter((i) => i.type === type).length,
  }));
  const totalIndications = indications.length;
  const photoCount = indications.filter((i) => i.type === 'Photo').length;

  // Per-date breakdown (all dates) with open / in-progress / resolved split.
  const perDateSummary = (() => {
    // Observation-aware: a persistent fault appears under EACH inspection day it
    // was observed on, with its CURRENT status (so "found in 2025, now resolved"
    // shows in 2025's resolved bucket — the maintenance overlay on that snapshot).
    const daysOf = (i: (typeof indications)[number]) => observationDays(i);
    const build = (inds: typeof indications, date: string) => {
      const resolved = inds.filter((i) => i.properties.status === 'resolved').length;
      const inProgress = inds.filter((i) => i.properties.status === 'in_progress').length;
      return { date, total: inds.length, open: inds.length - resolved - inProgress, inProgress, resolved };
    };
    const rows = availableDates
      .map((date) => build(indications.filter((i) => daysOf(i).includes(date)), date))
      .filter((r) => r.total > 0);
    const undated = indications.filter((i) => daysOf(i).length === 0);
    if (undated.length > 0) rows.push(build(undated, ''));
    return rows;
  })();

  return (
    <div className="ep-ticket-content ep-ticket-content--fill" data-testid="indications-ticket">
      {/* F-02E-2B: share viewers see no Add / Clear / orientation-write /
          string-modules controls. Add-Indication toggles add-mode (which
          opens a write modal on map click), Apply orientation buttons
          fire updateIndication writes, the orientation default + string
          modules inputs only seed new indications which can't be created.
          Download CSV is kept — visitor already sees the indications as
          map markers; the CSV is a read-only convenience. */}
      {!isReadOnly && (
        <>
          {/* Add mode toggle */}
          {isAddMode ? (
            <>
              <div className="add-mode-active">
                <span className="add-mode-label">
                  {quickAddIndicationType ? (
                    <>
                      <span
                        className="indication-summary__dot"
                        style={{ backgroundColor: INDICATION_TYPES[quickAddIndicationType].color }}
                      />
                      QUICK ADD: {INDICATION_TYPES[quickAddIndicationType].label}
                    </>
                  ) : (
                    'ADD MODE: click map...'
                  )}
                </span>
                <Button variant="ghost" size="sm" onClick={handleAddModeToggle}>Exit</Button>
              </div>
              {/* Pre-selected type ⇒ each map click creates it instantly (no
                  popup). Default "Ask each time" = the classic popup flow. */}
              <div className="add-mode-type">
                <label htmlFor="quick-add-type-select">Type</label>
                <select
                  id="quick-add-type-select"
                  className="indication-type-select"
                  value={quickAddIndicationType ?? ''}
                  onChange={(e) =>
                    setQuickAddIndicationType((e.target.value || null) as IndicationType | null)
                  }
                  data-testid="indications-ticket-quick-type"
                >
                  <option value="">Ask each time (popup)</option>
                  {Object.entries(INDICATION_TYPES).map(([type, config]) => (
                    <option key={type} value={type}>
                      {config.label}
                    </option>
                  ))}
                </select>
              </div>
            </>
          ) : (
            <Button variant="accent" size="sm" fullWidth onClick={handleAddModeToggle} data-testid="indications-ticket-add">
              Add Indication
            </Button>
          )}

          {/* PFC-1A: quick batch-classify of unclassified Photo indications.
              Gated by the enclosing !isReadOnly block so share visitors
              never see it. */}
          <Button
            variant="secondary"
            size="sm"
            fullWidth
            onClick={() => openPanel('photoClassifierPanel')}
            disabled={photoCount === 0}
            data-testid="indications-ticket-classify-photos"
          >
            Classify Photos{photoCount > 0 ? ` (${photoCount})` : ''}
          </Button>

          <div className="ep-divider" />
          <div className="ep-section-label">Orientation</div>
          <div className="orientation-row">
            <label className="orientation-label">New indication orientation</label>
            <div className="orientation-input-group">
              <input
                type="number" min="0" max="360" step="1"
                value={defaultIndicationOrientation}
                onChange={(e) => setDefaultIndicationOrientation(Number(e.target.value) % 360)}
                className="orientation-input"
              />
              <span className="orientation-unit">&deg;</span>
            </div>
          </div>
          <div className="orientation-buttons">
            <Button variant="secondary" size="sm" onClick={handleApplyOrientationToAll} disabled={indications.length === 0} title="Applies to the indications currently shown on the map (respects the date + type filters)">
              Apply to shown
            </Button>
            <Button variant="secondary" size="sm" onClick={handleFitToGrid} disabled={indications.length === 0} title="Resize + re-orient the shown markers to their module cells (positions and values unchanged; one Undo reverts). Markers off the grid are kept as-is.">
              Fit to grid (shown)
            </Button>
            <Button variant="secondary" size="sm" onClick={handleApplyOrientationToSelected} disabled={selectedFeatureIds.length === 0 && !selectedFeatureId}>
              Apply to selected{selectedFeatureIds.length > 1 ? ` (${selectedFeatureIds.length})` : ''}
            </Button>
            <Button
              variant={areaSelectMode ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setAreaSelectMode(!areaSelectMode)}
              title="Draw a polygon on the map to select every indication inside it. Click points, double-click (or Enter) to finish, Esc to cancel. Shift+drag also box-selects."
            >
              {areaSelectMode ? 'Drawing… (Esc cancels)' : 'Select area'}
            </Button>
          </div>
          {selectedFeatureIds.length > 0 && (
            <div className="orientation-selection-info">
              <span className="selection-count">{selectedFeatureIds.length} indication{selectedFeatureIds.length > 1 ? 's' : ''} selected</span>
              <span className="selection-hint">(Ctrl+click to add/remove)</span>
              <button
                type="button"
                className="selection-clear-btn"
                onClick={() => useMapStore.getState().clearSelection()}
                title="Clear the current selection"
              >
                Clear
              </button>
            </div>
          )}

          <div className="ep-divider" />
          <div className="ep-section-label">Batch Status</div>
          {batchStatusMode && (
            <div className="add-mode-active">
              <span className="add-mode-label">
                PAINT: click markers → {STATUS_LABELS[batchStatusTarget]}
              </span>
              <Button variant="ghost" size="sm" onClick={() => setBatchStatusMode(false)}>Exit</Button>
            </div>
          )}
          <div className="add-mode-type">
            <label htmlFor="batch-status-target-select">Set status to</label>
            <select
              id="batch-status-target-select"
              className="indication-type-select"
              value={batchStatusTarget}
              onChange={(e) => setBatchStatusTarget(e.target.value as IndicationStatus)}
              data-testid="indications-ticket-batch-status-target"
            >
              {STATUS_ORDER.map((s) => (
                <option key={s} value={s}>{STATUS_LABELS[s]}</option>
              ))}
            </select>
          </div>
          <div className="orientation-buttons">
            <Button
              variant={batchStatusMode ? 'accent' : 'secondary'}
              size="sm"
              onClick={() => setBatchStatusMode(!batchStatusMode)}
              disabled={indications.length === 0}
              title="While armed, clicking a marker applies the target status (Esc to exit)"
              data-testid="indications-ticket-batch-status-arm"
            >
              {batchStatusMode ? 'Exit paint mode' : 'Paint on click'}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleApplyStatusToSelected}
              disabled={isApplyingStatus || (selectedFeatureIds.length === 0 && !selectedFeatureId)}
              data-testid="indications-ticket-batch-status-apply"
            >
              Apply to selected{selectedFeatureIds.length > 1 ? ` (${selectedFeatureIds.length})` : ''}
            </Button>
          </div>

          <div className="ep-divider" />
          <div className="ep-section-label">History</div>
          <div className="orientation-buttons">
            <Button variant="secondary" size="sm" onClick={toggleOps}>
              {opsOpen ? 'Hide recent operations' : 'Recent operations'}
            </Button>
            <Button variant="secondary" size="sm" onClick={handleRederiveSite} disabled={isApplyingStatus} title="Scan every fault against the module grid and preview the corrections — nothing is written until you approve; one Undo reverts the whole run.">
              Re-derive site from grid…
            </Button>
          </div>
          {rederivePlan && (
            <SiteRederiveModal
              siteName={useProjectStore.getState().meta.siteName || 'site'}
              plan={rederivePlan}
              onApply={executeRederive}
              onCancel={() => setRederivePlan(null)}
            />
          )}
          {opsOpen && (
            <div className="op-history">
              {ops === null && <div className="op-history__empty">Loading…</div>}
              {ops !== null && ops.length === 0 && <div className="op-history__empty">No batch operations yet</div>}
              {ops?.map((op) => (
                <div key={op.id} className="op-history__row">
                  <div className="op-history__info">
                    <span className="op-history__label">{op.label ?? op.kind}</span>
                    <span className="op-history__meta">
                      {op.change_count} row{op.change_count === 1 ? '' : 's'} · {new Date(op.created_at + (op.created_at.endsWith('Z') ? '' : 'Z')).toLocaleTimeString()} ·{' '}
                      {op.status === 'active' ? (op.kind === 'undo' ? 'undo' : 'active') : op.status.replace('_', ' ')}
                    </span>
                  </div>
                  {(op.status === 'active' || op.status === 'partially_undone') && (
                    <button type="button" className="selection-clear-btn" onClick={() => { void runUndo(op.id); }}>
                      Undo
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="ep-divider" />
          <div className="ep-section-label">String OC Length</div>
          <div className="orientation-row">
            <label className="orientation-label">Default modules per string</label>
            <div className="orientation-input-group">
              <input
                type="number" min="1" max="200" step="1"
                value={defaultStringModules}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  if (!isNaN(v)) setDefaultStringModules(v);
                }}
                className="orientation-input"
              />
              <span className="orientation-unit">mod</span>
            </div>
          </div>

          <div className="ep-divider" />
          <div className="ep-section-label">Observation Time</div>
          <label className="orientation-label observation-time-label">For new indications</label>
          <input
            type="datetime-local"
            value={defaultObservationTime ?? ''}
            onChange={(e) => setDefaultObservationTime(e.target.value || null)}
            className="observation-time-input"
          />
          <div className="orientation-buttons">
            <Button variant="secondary" size="sm" onClick={() => setDefaultObservationTime(localDatetimeNow())}>
              Set to now
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setDefaultObservationTime(null)} disabled={!defaultObservationTime}>
              Clear
            </Button>
          </div>
          <div className="ep-divider" />
        </>
      )}
      <Button variant="secondary" size="sm" fullWidth onClick={handleExportCSV} disabled={totalIndications === 0}>
        Download CSV
      </Button>
      {!isReadOnly && (
        <Button variant="ghost" size="sm" fullWidth onClick={handleClear} disabled={totalIndications === 0} data-testid="indications-ticket-clear">
          Clear All
        </Button>
      )}

      {/* Indication summary */}
      {totalIndications > 0 && (
        <>
          <div className="ep-divider" />
          <div className="ep-section-label">Summary ({totalIndications})</div>
          <div className="indication-summary-scroll">
          <div className="indication-summary">
            {indicationCounts.filter((i) => i.count > 0).map((item) => (
              <div key={item.type} className="indication-summary__item">
                <span className="indication-summary__dot" style={{ backgroundColor: item.color }} />
                <span className="indication-summary__label">{item.label}</span>
                <span className="indication-summary__count">{item.count}</span>
              </div>
            ))}
          </div>
          {perDateSummary.length > 0 && (
            <details className="summary-by-date" open>
              <summary className="summary-by-date__toggle">By date ({perDateSummary.length})</summary>
              <div className="summary-by-date__list">
                {perDateSummary.map((r) => (
                  <div key={r.date || 'undated'} className="summary-date">
                    <div className="summary-date__head">
                      <span className="summary-date__label">{formatDay(r.date)}</span>
                      <span className="summary-date__count">{r.total}</span>
                    </div>
                    <div className="summary-date__breakdown">
                      {r.open} open
                      {r.inProgress > 0 && ` · ${r.inProgress} in progress`}
                      {r.resolved > 0 && ` · ${r.resolved} resolved`}
                    </div>
                  </div>
                ))}
              </div>
            </details>
          )}
          </div>
        </>
      )}
      {totalIndications === 0 && <p className="ep-empty-text">No indications yet</p>}
    </div>
  );
}
